import { useContext, useEffect, useState } from "react";

import { DndIcon } from "~/components/DndIcon";
import { PresenceIndicator } from "~/components/PresenceIndicator";
import { MatrixContext } from "~/context/MatrixContext";
import { useUserStatus, type UserStatus } from "~/hooks/useMyStatus";
import type { PresenceState } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

function formatRemaining(expiresAt: number | null): string {
  if (!expiresAt) return "";
  const remaining = Math.max(0, expiresAt - Date.now());
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes < 60) return ` for ${minutes}m more`;
  const hours = Math.ceil(minutes / 60);
  if (hours < 24) return ` for ${hours}h more`;
  const days = Math.ceil(hours / 24);
  return ` for ${days}d more`;
}

export function statusTooltip(status: UserStatus): string {
  return `${status.message}${formatRemaining(status.expiresAt)}`;
}

export function StatusGlyph({
  status,
  dndActive,
  presence,
  surface = "panel",
}: {
  status?: UserStatus | null;
  dndActive?: boolean;
  presence?: PresenceState;
  surface?: "panel" | "app";
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!status?.expiresAt) return;
    const id = window.setInterval(() => setTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(id);
  }, [status?.expiresAt]);

  if (status) {
    const tooltip = statusTooltip(status);
    return (
      <span
        role="img"
        aria-label={tooltip}
        title={tooltip}
        style={{
          position: "absolute",
          right: -3,
          bottom: -3,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "var(--chat-radius-full)",
          background: surface === "app" ? "var(--chat-surface-app)" : "var(--chat-surface-panel)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: 10,
          lineHeight: 1,
          boxShadow: `0 0 0 1px ${surface === "app" ? "var(--chat-surface-app)" : "var(--chat-surface-panel)"}`,
          pointerEvents: "auto",
        }}
      >
        {status.emoji}
      </span>
    );
  }
  if (dndActive) return <DndCrescent surface={surface} />;
  if (presence) return <PresenceIndicator state={presence} surface={surface} />;
  return null;
}

export function DndCrescent({ surface = "panel" }: { surface?: "panel" | "app" }) {
  return (
    <span
      role="img"
      aria-label="Do not disturb"
      style={{
        position: "absolute",
        right: -3,
        bottom: -3,
        width: 14,
        height: 14,
        borderRadius: "var(--chat-radius-full)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: surface === "app" ? "var(--chat-surface-app)" : "var(--chat-surface-panel)",
        color: "var(--chat-fg-muted)",
      }}
    >
      <DndIcon size={11} strokeWidth={ICON_STROKE} aria-hidden />
    </span>
  );
}

export function UserStatusGlyph({
  userId,
  surface,
}: {
  userId: string | null;
  surface?: "panel" | "app";
}) {
  const matrix = useContext(MatrixContext);
  const status = useUserStatus(matrix?.client ?? null, userId);
  return <StatusGlyph status={status} surface={surface} />;
}
